<template>
    <ModalShell title="Add Email" subtitle="Enter the email address you'd like to add to your Identity Vault" divider-class="mt-4 mb-3">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                <path d="M18.25 0.25H1.75C1.35218 0.25 0.970644 0.408035 0.68934 0.68934C0.408035 0.970644 0.25 1.35218 0.25 1.75V14.5C0.25 14.8978 0.408035 15.2794 0.68934 15.5607C0.970644 15.842 1.35218 16 1.75 16H3.25V17.5C3.25 17.6989 3.32902 17.8897 3.46967 18.0303C3.61032 18.171 3.80109 18.25 4 18.25C4.19891 18.25 4.38968 18.171 4.53033 18.0303C4.67098 17.8897 4.75 17.6989 4.75 17.5V16H15.25V17.5C15.25 17.6989 15.329 17.8897 15.4697 18.0303C15.6103 18.171 15.8011 18.25 16 18.25C16.1989 18.25 16.3897 18.171 16.5303 18.0303C16.671 17.8897 16.75 17.6989 16.75 17.5V16H18.25C18.6478 16 19.0294 15.842 19.3107 15.5607C19.592 15.2794 19.75 14.8978 19.75 14.5V1.75C19.75 1.35218 19.592 0.970644 19.3107 0.68934C19.0294 0.408035 18.6478 0.25 18.25 0.25ZM18.25 14.5H1.75V1.75H18.25V7.75H16.6863C16.4982 6.63745 15.899 5.63615 15.0075 4.94452C14.116 4.25289 12.9972 3.9214 11.8729 4.01576C10.7485 4.11012 9.70059 4.62343 8.93683 5.45397C8.17307 6.28451 7.74919 7.37167 7.74919 8.5C7.74919 9.62833 8.17307 10.7155 8.93683 11.546C9.70059 12.3766 10.7485 12.8899 11.8729 12.9842C12.9972 13.0786 14.116 12.7471 15.0075 12.0555C15.899 11.3639 16.4982 10.3625 16.6863 9.25H18.25V14.5ZM13.5475 7.75C13.3824 7.46404 13.1276 7.24054 12.8225 7.11418C12.5174 6.98782 12.1792 6.96565 11.8602 7.05111C11.5413 7.13657 11.2594 7.32489 11.0584 7.58686C10.8574 7.84882 10.7485 8.1698 10.7485 8.5C10.7485 8.8302 10.8574 9.15118 11.0584 9.41314C11.2594 9.67511 11.5413 9.86343 11.8602 9.94889C12.1792 10.0344 12.5174 10.0122 12.8225 9.88582C13.1276 9.75946 13.3824 9.53596 13.5475 9.25H15.1562C14.9737 9.95699 14.5396 10.5731 13.9352 10.983C13.3309 11.3928 12.5979 11.5681 11.8735 11.4761C11.1491 11.3841 10.4832 11.0311 10.0005 10.4832C9.51782 9.93529 9.25151 9.23018 9.25151 8.5C9.25151 7.76982 9.51782 7.0647 10.0005 6.51683C10.4832 5.96895 11.1491 5.61592 11.8735 5.52391C12.5979 5.43191 13.3309 5.60724 13.9352 6.01705C14.5396 6.42686 14.9737 7.04301 15.1562 7.75H13.5475Z" fill="currentColor" />
            </svg>
        </template>
        <ModalField
            label="Email Address"
            input-id="add-email-input"
            :error="isEmailInvalid ? 'Please enter a valid email address.' : undefined"
            v-slot="{ errorId, invalid }"
        >
            <prime-input-text
                id="add-email-input"
                v-model="emailInput"
                class="w-full"
                placeholder="john@privacymail.com"
                :aria-describedby="errorId"
                @keyup.enter="sendVerificationCode"
                :invalid="invalid"
            />
        </ModalField>
        <template #footer>
            <prime-button @click="closeDialog" class="p-btn-root_secondary" label="Cancel" />

            <prime-button
                label="Send Verification Code"
                variant="primary"
                @click="sendVerificationCode"
                :loading="isSendingVerificationCode"
                :disabled="emailInput.trim() === '' || isEmailInvalid"
            />
        </template>
    </ModalShell>
</template>
<script setup lang="ts">
import { isValidEmail } from '@privacy-bee/validate/email';
import ModalField from '~/components/modals/shared/ModalField.vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { ModalNames } from '~/types/modals';

const emailInput = ref('');
const { showError } = useGlobalToast();

const isEmailInvalid = computed(() => emailInput.value.trim() !== '' && !isValidEmail(emailInput.value.trim()));

const dialogRef = getDialogRef(ModalNames.ADD_EMAIL);
const closeDialog = () => {
    dialogRef.value.close();
};

// Get modal data if provided
const modalData = computed(() => dialogRef.value?.data || {});

// Watch for modal data changes and prefill
watch(() => modalData.value?.email, (emailData) => {
    if (emailData && typeof emailData === 'string') {
        emailInput.value = emailData;
    }
}, { immediate: true });

const personStore = usePersonStore();
const isSendingVerificationCode = ref(false);

const sendVerificationCode = async () => {
    if (emailInput.value === '') return;
    isSendingVerificationCode.value = true;

    try {
        // Pass exposure data to the verification modal if this came from an exposure
        const additionalData = modalData.value?.fromExposure ? {
            fromExposure: modalData.value.fromExposure,
            exposureData: modalData.value.exposureData
        } : undefined;

            // Check if email already exists
        const existingEmail = personStore.person?.emails?.find(email => 
            email.email === emailInput.value
        );

        if (existingEmail) {
            // Add 1 second delay to show loading
            await new Promise(resolve => setTimeout(resolve, 1000));
            showError('Error', 'This email already exists in your vault');
            isSendingVerificationCode.value = false;
            return;
        }

        await personStore.sendEmailVerificationCode(emailInput.value, false, additionalData);
        isSendingVerificationCode.value = false;
        dialogRef.value.close();
    } catch (error) {
        isSendingVerificationCode.value = false;
        return;
    }
};
</script>
<style lang="scss" scoped>
.footer-actions {
    .p-btn-root_primary {
        @apply sm:px-3 px-1 py-2;
    }
}
</style>
